'use client';

import { useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import type { TitleOption } from 'echarts/types/dist/shared';
import { useResizeObserver } from '../../hooks/use-resize-observer';
import { 
  defaultTheme, defaultThemeLight, defaultThemeDark,
  softCosmicFlareThemeLight, softCosmicFlareThemeDark,
  gentlePhotonStreamThemeLight, gentlePhotonStreamThemeDark,
  pastelAzureThemeLight, pastelAzureThemeDark
} from './themes';
import { useTheme } from '../theme-provider';

type ThemeName = 'tablab' | 'soft-cosmic-flare' | 'gentle-photon-stream' | 'pastel-azure';

export interface TabLabEChartsProps {
  option: echarts.EChartsOption;
  className?: string;
  loading?: boolean;
  theme?: echarts.EChartsOption;
  themeName?: ThemeName;
}

export function TabLabECharts({ 
  option, 
  className = '', 
  loading = false,
  theme = defaultTheme,
  themeName = 'tablab'
}: TabLabEChartsProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const prevOptionRef = useRef<echarts.EChartsOption>(option);
  const { theme: currentTheme } = useTheme();
  const isDarkMode = currentTheme === 'dark';

  // Extract title from options for comparison
  const currentTitle = useMemo(() => {
    if (!option.title) return null;
    const titleOption = option.title as TitleOption;
    return titleOption.text || null;
  }, [option]);

  // Get the appropriate theme based on mode and theme name
  const getThemeObject = (themeName: ThemeName, isDark: boolean): echarts.EChartsOption => {
    switch (themeName) {
      case 'tablab':
        return isDark ? defaultThemeDark : defaultThemeLight;
      case 'soft-cosmic-flare':
        return isDark ? softCosmicFlareThemeDark : softCosmicFlareThemeLight;
      case 'gentle-photon-stream':
        return isDark ? gentlePhotonStreamThemeDark : gentlePhotonStreamThemeLight;
      case 'pastel-azure':
        return isDark ? pastelAzureThemeDark : pastelAzureThemeLight;
      default:
        return isDark ? defaultThemeDark : defaultThemeLight;
    }
  };

  // Register all themes once
  useEffect(() => {
    // Register light mode themes
    echarts.registerTheme('tablab-light', defaultThemeLight);
    echarts.registerTheme('soft-cosmic-flare-light', softCosmicFlareThemeLight);
    echarts.registerTheme('gentle-photon-stream-light', gentlePhotonStreamThemeLight);
    echarts.registerTheme('pastel-azure-light', pastelAzureThemeLight);
    
    // Register dark mode themes
    echarts.registerTheme('tablab-dark', defaultThemeDark);
    echarts.registerTheme('soft-cosmic-flare-dark', softCosmicFlareThemeDark);
    echarts.registerTheme('gentle-photon-stream-dark', gentlePhotonStreamThemeDark);
    echarts.registerTheme('pastel-azure-dark', pastelAzureThemeDark);
  }, []);

  // Get the theme name with mode suffix
  const getThemeName = (baseName: ThemeName, isDark: boolean): string => {
    return `${baseName}-${isDark ? 'dark' : 'light'}`;
  };

  // Initialize chart
  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize with selected theme based on current mode
    const themeToUse = getThemeName(themeName, isDarkMode);
    
    chartInstance.current = echarts.init(chartRef.current, themeToUse, {
      renderer: 'canvas'
    });

    // Get the current theme object
    const currentThemeObj = getThemeObject(themeName, isDarkMode);
    
    // Deep merge theme series settings with options
    const mergedOption = { ...option };
    
    // If there are series in both the theme and the options, ensure theme settings are preserved
    if (currentThemeObj.series && Array.isArray(currentThemeObj.series) && 
        mergedOption.series && Array.isArray(mergedOption.series)) {
      
      mergedOption.series = mergedOption.series.map((series, index) => {
        // Find matching series type in theme
        const themeSeries = Array.isArray(currentThemeObj.series) 
          ? currentThemeObj.series.find((s: any) => s.type === series.type) 
          : undefined;
          
        if (themeSeries) {
          // Use type assertion to help TypeScript understand the structure
          const themeSeriesAny = themeSeries as any;
          const seriesAny = series as any;
          
          // Deep merge label settings from theme
          if (themeSeriesAny.label && seriesAny.label) {
            seriesAny.label = {
              ...themeSeriesAny.label,
              ...seriesAny.label,
              // Ensure theme color and styling are preserved
              color: themeSeriesAny.label.color,
              fontWeight: themeSeriesAny.label.fontWeight,
              textBorderColor: themeSeriesAny.label.textBorderColor,
              textBorderWidth: themeSeriesAny.label.textBorderWidth,
              textShadowBlur: themeSeriesAny.label.textShadowBlur,
              textShadowColor: themeSeriesAny.label.textShadowColor
            };
            return seriesAny;
          }
        }
        return series;
      });
    }

    // Initial render with merged options
    chartInstance.current.setOption(mergedOption, { notMerge: true });
    prevOptionRef.current = mergedOption;

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, [themeName, isDarkMode, option]); // Re-initialize when theme, mode, or options change

  // Update options when they change
  useEffect(() => {
    if (!chartInstance.current || !prevOptionRef.current) return;

    const prevTitleOption = prevOptionRef.current.title as TitleOption;
    const prevTitle = prevTitleOption?.text || null;

    // If only the title changed, update just the title
    if (currentTitle !== prevTitle && 
        JSON.stringify({ ...option, title: null }) === JSON.stringify({ ...prevOptionRef.current, title: null })) {
      chartInstance.current.setOption({ title: option.title }, { notMerge: false });
    } else {
      // For other changes, do a full update with notMerge: true
      chartInstance.current.setOption(option, { notMerge: true });
    }

    prevOptionRef.current = option;
  }, [option, currentTitle]);

  // Handle loading state
  useEffect(() => {
    if (!chartInstance.current) return;

    const currentThemeObj = getThemeObject(themeName, isDarkMode);
    
    if (loading) {
      chartInstance.current.showLoading({
        text: 'Loading...',
        color: (currentThemeObj.color as string[])?.[0] ?? '#1d4ed8',
        textColor: currentThemeObj.textStyle?.color ?? (isDarkMode ? '#D1D5DB' : '#1d4ed8'),
        maskColor: isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
        fontSize: 16,
        showSpinner: true,
        spinnerRadius: 6,
      });
    } else {
      chartInstance.current.hideLoading();
    }
  }, [loading, themeName, isDarkMode]);

  // Handle theme changes
  useEffect(() => {
    if (!chartInstance.current) return;
    
    // Dispose and reinitialize with new theme when dark mode changes
    const themeToUse = getThemeName(themeName, isDarkMode);
    const currentOption = chartInstance.current.getOption();
    
    chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current!, themeToUse, {
      renderer: 'canvas'
    });
    
    chartInstance.current.setOption(currentOption);
  }, [isDarkMode, themeName]);

  // Handle resize
  const onResize = () => {
    if (chartInstance.current) {
      chartInstance.current.resize();
    }
  };

  // Only call useResizeObserver if chartRef.current exists
  useEffect(() => {
    if (chartRef.current) {
      const resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(chartRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  return (
    <div 
      ref={chartRef} 
      className={`w-full h-full ${className}`}
      style={{ 
        position: 'relative',
        width: '100%',
        height: '100%'
      }}
    />
  );
} 